import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mentionsApi, type MentionRow } from '@/api/mentions.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useToasts } from '@/context/ToastContext';
import { formatRelative } from '@/lib/format';

/**
 * V4.9.5 — My Tags inbox.
 *
 * Lists every ticket where someone @-mentioned this user. Unread rows are
 * highlighted with a blue left border. Clicking a row marks it read and
 * navigates to the ticket. There's also a "Mark all read" button.
 */
export function MyTagsPage() {
  const [tab, setTab] = React.useState<'all' | 'unread'>('unread');
  const queryClient = useQueryClient();
  const { push } = useToasts();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['my-tags', tab],
    queryFn: () => mentionsApi.list(tab === 'unread')
  });

  const markRead = useMutation({
    mutationFn: (id: string) => mentionsApi.markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-tags'] });
      void queryClient.invalidateQueries({ queryKey: ['my-tags-unread-count'] });
    }
  });

  const markAllRead = useMutation({
    mutationFn: () => mentionsApi.markAllRead(),
    onSuccess: (res) => {
      push({
        kind: 'success',
        title: `Marked ${res.updated} mention${res.updated === 1 ? '' : 's'} as read`
      });
      void queryClient.invalidateQueries({ queryKey: ['my-tags'] });
      void queryClient.invalidateQueries({ queryKey: ['my-tags-unread-count'] });
    },
    onError: () => {
      push({ kind: 'error', title: 'Failed to mark all read' });
    }
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error ?? new Error('Failed')} onRetry={() => refetch()} />;

  const rows: MentionRow[] = data ?? [];
  const unreadCount = rows.filter((r) => !r.read_at).length;

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>My Tags</h2>
          <p className="text-small text-muted" style={{ marginTop: 4 }}>
            Tickets where someone @-mentioned you.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className={`btn btn-sm ${tab === 'unread' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('unread')}
          >
            Unread{tab === 'unread' && unreadCount ? ` (${unreadCount})` : ''}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${tab === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('all')}
          >
            All
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={markAllRead.isPending || rows.every((r) => r.read_at)}
            onClick={() => markAllRead.mutate()}
          >
            {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="state">
          <h4>{tab === 'unread' ? 'No unread mentions' : 'No mentions yet'}</h4>
          <p className="text-small">
            {tab === 'unread'
              ? "When someone tags you with @yourname, you'll see it here."
              : 'When someone tags you with @yourname, every mention will appear here.'}
          </p>
        </div>
      ) : (
        <div className="my-tags-list">
          {rows.map((m) => (
            <Link
              key={m.id}
              to={`/tickets/${m.ticket_id}`}
              state={{ from: '/my-tags' }}
              className={`my-tag-row ${m.read_at ? '' : 'unread'}`}
              onClick={() => {
                if (!m.read_at) markRead.mutate(m.id);
              }}
            >
              <div className="my-tag-meta">
                <span className="my-tag-ticket-id">{m.ticket_id}</span>
                {m.ticket_category ? <span>{m.ticket_category}</span> : null}
                {m.ticket_status ? <span>{m.ticket_status}</span> : null}
                <span style={{ marginLeft: 'auto' }}>{formatRelative(m.created_at)}</span>
              </div>
              <div className="my-tag-excerpt">
                <span className="my-tag-actor">{m.mentioned_by?.name ?? 'Someone'}</span>
                {': '}
                {m.excerpt}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
